import { useRef, useState } from 'react';
import { Banknote, CreditCard, QrCode, Wallet, Loader2, RefreshCw, Lock, Unlock } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ShiftSummaryCard } from '@/components/pos/ShiftSummaryCard';
import { CashCountPanel } from '@/components/pos/CashCountPanel';
import { MoneyValue } from '@/components/pos/MoneyValue';
import { EmptyState } from '@/components/pos/EmptyState';
import {
  useShiftSummary,
  useCurrentShift,
  useOpenShift,
  useCloseShift,
} from '@/hooks/queries';
import { todayISO, formatDate, formatTime, rupiah } from '@/lib/format';

const EMPTY = { count: 0, gross: 0, discount: 0 };

export function ShiftSummaryPage() {
  const today = todayISO();
  const { data, isLoading, isFetching, isError, refetch } = useShiftSummary(today);
  const { data: current, isLoading: shiftLoading } = useCurrentShift();
  const openMut = useOpenShift();
  const closeMut = useCloseShift();

  const [floatInput, setFloatInput] = useState('');
  const countRef = useRef<{ counted: number; denoms: Record<string, number> }>({
    counted: 0,
    denoms: {},
  });

  const cash = data?.byMethod.cash ?? EMPTY;
  const emoney = data?.byMethod['e-money'] ?? EMPTY;
  const qris = data?.byMethod.midtrans ?? EMPTY;
  const grandGross = cash.gross + emoney.gross + qris.gross;
  const grandCount = cash.count + emoney.count + qris.count;
  // While a shift is open, the drawer should also hold the opening float.
  const openingFloat = current?.openingFloat ?? 0;
  const expectedCash = openingFloat + cash.gross;

  const handleOpen = () => {
    openMut.mutate(Math.max(0, Number(floatInput) || 0), {
      onSuccess: () => {
        toast.success('Shift dibuka.');
        setFloatInput('');
      },
      onError: (e: { message?: string }) => toast.error(e?.message ?? 'Gagal membuka shift.'),
    });
  };

  const handleClose = () => {
    const { counted, denoms } = countRef.current;
    closeMut.mutate(
      { countedCash: counted, denominations: denoms },
      {
        onSuccess: (shift) => {
          const v = shift.variance ?? 0;
          const detail =
            v === 0 ? 'Kas sesuai.' : v > 0 ? `Lebih ${rupiah(v)}.` : `Kurang ${rupiah(Math.abs(v))}.`;
          toast.success(`Shift ditutup. ${detail}`);
        },
        onError: (e: { message?: string }) => toast.error(e?.message ?? 'Gagal menutup shift.'),
      },
    );
  };

  return (
    <div className="mx-auto h-full max-w-4xl overflow-y-auto px-6 py-8 pos-scroll">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-ink-900">Ringkasan Shift</h1>
          <p className="text-sm text-muted-foreground">{formatDate(today)}</p>
        </div>
        <Button variant="outline" className="gap-2" onClick={() => refetch()} disabled={isFetching}>
          {isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Segarkan
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20 text-ink-400">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : !data ? (
        <EmptyState
          icon={Wallet}
          title="Ringkasan belum tersedia"
          description={
            isError
              ? 'Gagal memuat ringkasan shift. Coba segarkan.'
              : 'Endpoint ringkasan shift belum aktif. Hubungi administrator.'
          }
          action={
            <Button variant="outline" onClick={() => refetch()}>
              Coba lagi
            </Button>
          }
        />
      ) : (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-3">
            <ShiftSummaryCard label="Tunai" icon={Banknote} count={cash.count} gross={cash.gross} discount={cash.discount} accent="crimson" />
            <ShiftSummaryCard label="E-Money" icon={CreditCard} count={emoney.count} gross={emoney.gross} discount={emoney.discount} />
            <ShiftSummaryCard label="QRIS" icon={QrCode} count={qris.count} gross={qris.gross} discount={qris.discount} />
          </div>

          <Card>
            <CardContent className="flex flex-col gap-4 pt-6 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">Total bruto hari ini</p>
                <MoneyValue value={grandGross} emphasis className="text-4xl" />
                <p className="tnum mt-1 text-sm text-ink-400">{grandCount} transaksi</p>
              </div>
              <div className="rounded-xl border-2 border-gold-500/40 bg-gold-200/40 px-6 py-4 text-center">
                <p className="text-xs font-semibold uppercase tracking-wide text-ink-600">
                  Kas tunai diharapkan
                </p>
                <MoneyValue value={expectedCash} emphasis className="text-2xl" />
                <p className="mt-1 text-xs text-ink-400">
                  {openingFloat > 0 ? `Modal ${rupiah(openingFloat)} + tunai` : 'Untuk rekonsiliasi laci kas'}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Till session — open, or count + close */}
          {shiftLoading ? (
            <div className="flex justify-center py-4 text-ink-400">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : !current ? (
            <Card>
              <CardContent className="flex flex-col gap-4 pt-6 sm:flex-row sm:items-end sm:justify-between">
                <div className="min-w-0">
                  <p className="font-display text-base font-semibold text-ink-900">Belum ada shift berjalan</p>
                  <p className="mt-0.5 text-sm text-muted-foreground">
                    Buka shift untuk mulai merekonsiliasi laci kas.
                  </p>
                </div>
                <div className="flex items-end gap-2">
                  <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-ink-400">
                    Modal awal
                    <div className="flex items-center rounded-lg border border-cream-200 bg-white">
                      <span className="pl-2.5 text-sm text-ink-400">Rp</span>
                      <input
                        type="number"
                        inputMode="numeric"
                        min={0}
                        value={floatInput}
                        onChange={(e) => setFloatInput(e.target.value)}
                        placeholder="0"
                        className="tnum h-9 w-28 bg-transparent px-2 text-sm text-ink-900 outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
                      />
                    </div>
                  </label>
                  <Button className="gap-2" onClick={handleOpen} disabled={openMut.isPending}>
                    {openMut.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Unlock className="h-4 w-4" />
                    )}
                    Buka shift
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-emerald-500/25 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-800">
                <span className="flex items-center gap-2 font-medium">
                  <Unlock className="h-4 w-4" /> Shift berjalan sejak {formatTime(current.openedAt)}
                </span>
                {openingFloat > 0 && (
                  <span className="tnum text-emerald-700">Modal awal {rupiah(openingFloat)}</span>
                )}
              </div>

              <CashCountPanel
                expectedCash={expectedCash}
                onChange={(counted, denoms) => {
                  countRef.current = { counted, denoms };
                }}
              />

              <Button
                className="w-full gap-2"
                onClick={handleClose}
                disabled={closeMut.isPending}
              >
                {closeMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                Tutup shift
              </Button>
            </>
          )}

          <Button variant="outline" className="w-full gap-2" onClick={() => window.print()}>
            Cetak Laporan Shift
          </Button>
        </div>
      )}
    </div>
  );
}
